var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { colors } from "healthex-common/colors";
import { FeatureFlag } from "healthex-common/features.dtos";
import { HealthWalletDetailsRecordType, HealthWalletRecordType, isBrowserDisplayableContentType, isSandboxedHtmlContentType, MeasurementInsightRangeStatus, ProvenanceSource, } from "healthex-common/health.wallet.dtos";
import { unslugify } from "healthex-common/utils";
import { action, computed, observable, runInAction } from "mobx";
import { Disposer } from "../Infra/Disposer";
import { getObservableT } from "../Infra/i18next";
import { NOT_LOADED } from "../Infra/RemoteData";
import { downloadURL } from "./DownloadHelpers";
import { HXMenuController } from "./HXMenu";
import { formatDisplayDate, HistoryCategory, IMMUNIZATION_TYPE_TO_CATEGORY, immunizationTypeDisplay, PROCEDURE_TYPE_TO_CATEGORY, procedureTypeDisplay, provenanceSourceDisplay, VISIT_TYPE_TO_CATEGORY, visitTypeDisplay, } from "./PatientHistory.types";
import { applyPrintDialogOnlyStyles } from "./PatientTimelineDetailsView.styles";
import { printScreen } from "./printScreen";
import { openSanitizedHtmlInTab } from "./sandboxedHtmlViewer";
const t = getObservableT("patients");
export class PatientTimelineDetailsViewModelProps {
    constructor(container, modalRecordId, modalRecordType) {
        this.container = container;
        this.modalRecordId = modalRecordId;
        this.modalRecordType = modalRecordType;
    }
}
function formatFileSize(bytes) {
    if (bytes === undefined || bytes === null || bytes === 0)
        return "";
    if (bytes < 1024)
        return `${bytes} B`;
    return `${Math.round(bytes / 1024)} KB`;
}
export function measurementDotColor(m) {
    return m.insightRangeStatus !== undefined ? insightRangeStatusColor(m.insightRangeStatus) : colors.history_secondary_text;
}
export function insightRangeStatusColor(status) {
    switch (status) {
        case MeasurementInsightRangeStatus.NORMAL:
            return colors.measurement_normal;
        case MeasurementInsightRangeStatus.ABNORMAL:
            return colors.measurement_abnormal;
        default:
            return colors.history_secondary_text;
    }
}
export class PatientTimelineDetailsViewModel {
    constructor(props) {
        this.disposer = new Disposer();
        this.fetchSequence = 0;
        this.insightFetchSequence = 0;
        this.moreMenuController = new HXMenuController();
        this.details = NOT_LOADED;
        this.isPrinting = false;
        this.downloadingDocumentId = null;
        this.measurementDialogOpen = false;
        this.selectedMeasurementName = null;
        this.selectedMeasurementObservationId = null;
        this.measurementInsight = NOT_LOADED;
        this.vaccinationInsightDialogOpen = false;
        this.selectedVaccinationTitle = null;
        this.vaccinationInsight = NOT_LOADED;
        this.vaccinationInsightFetchSequence = 0;
        this.printStyleCleanup = null;
        this.lastLoadRequestedKey = null;
        this.healthWalletService = props.container.healthWalletService;
        this.routingUIManager = props.container.routingUIManager;
        this.authManager = props.container.authManager;
        this.bannerUIManager = props.container.bannerUIManager;
        this.unitSystemManager = props.container.unitSystemManager;
        this.featureManager = props.container.featureManager;
        this.modalRecordId = props.modalRecordId;
        this.modalRecordType = props.modalRecordType;
        this.browserLanguageUIManager = props.container.browserLanguageUIManager;
    }
    get hasLoadedFeatures() {
        return this.featureManager.hasLoadedFeatures;
    }
    get isHealthWalletEnabled() {
        if (!this.featureManager.hasLoadedFeatures) {
            return false;
        }
        return this.featureManager.isFeatureEnabled(FeatureFlag.RO_HEALTH_WALLET_ENABLED);
    }
    run() {
        if (this.modalRecordId) {
            this.printStyleCleanup = applyPrintDialogOnlyStyles();
        }
        this.disposer.reaction(() => [
            this.resourceId,
            this.recordType,
            this.authManager.id,
            this.browserLanguageUIManager.currentLanguage,
            this.unitSystemManager.unitSystem,
        ], () => {
            if (!this.resourceId || !this.recordType || !this.authManager.id)
                return;
            void this.loadDetails();
        }, { fireImmediately: true, name: "historyDetails-load" });
        this.disposer.reaction(() => [
            this.measurementDialogOpen,
            this.selectedMeasurementObservationId,
            this.browserLanguageUIManager.currentLanguage,
            this.unitSystemManager.unitSystem,
            this.authManager.id,
        ], () => {
            if (!this.measurementDialogOpen)
                return;
            if (!this.selectedMeasurementObservationId || !this.authManager.id)
                return;
            void this.loadMeasurementInsight(this.selectedMeasurementObservationId);
        }, { name: "historyDetails-measurementInsight" });
        this.disposer.reaction(() => [this.vaccinationInsightDialogOpen, this.resourceId, this.browserLanguageUIManager.currentLanguage, this.authManager.id], () => {
            if (!this.vaccinationInsightDialogOpen)
                return;
            if (!this.resourceId || !this.authManager.id)
                return;
            void this.loadVaccinationInsight(this.resourceId);
        }, { name: "historyDetails-vaccinationInsight" });
    }
    stop() {
        var _a;
        (_a = this.printStyleCleanup) === null || _a === void 0 ? void 0 : _a.call(this);
        this.printStyleCleanup = null;
        this.disposer.dispose();
        this.closeMeasurementDialog();
        this.closeVaccinationInsightDialog();
    }
    get resourceId() {
        var _a, _b, _c;
        return (_c = (_a = this.modalRecordId) !== null && _a !== void 0 ? _a : (_b = this.routingUIManager.currentRouteParams) === null || _b === void 0 ? void 0 : _b.id) !== null && _c !== void 0 ? _c : null;
    }
    get recordType() {
        var _a, _b;
        const raw = (_a = this.modalRecordType) !== null && _a !== void 0 ? _a : (_b = this.routingUIManager.currentRouteParams) === null || _b === void 0 ? void 0 : _b.recordType;
        if (!raw)
            return null;
        const value = unslugify(raw);
        if (value === HealthWalletDetailsRecordType.VISIT ||
            value === HealthWalletDetailsRecordType.PROCEDURE ||
            value === HealthWalletDetailsRecordType.IMMUNIZATION) {
            return value;
        }
        return null;
    }
    async loadDetails() {
        var _a;
        if (!this.resourceId || !this.recordType || !this.authManager.id)
            return;
        const key = `${this.recordType}:${this.resourceId}:${this.browserLanguageUIManager.currentLanguage}:${this.unitSystemManager.unitSystem}`;
        if (this.lastLoadRequestedKey === key)
            return;
        this.lastLoadRequestedKey = key;
        const seq = ++this.fetchSequence;
        try {
            const result = await this.healthWalletService.getDetails(this.authManager.id, this.recordType, this.resourceId, this.browserLanguageUIManager.currentLanguage, (_a = this.unitSystemManager.unitSystem) !== null && _a !== void 0 ? _a : undefined);
            if (seq !== this.fetchSequence)
                return;
            runInAction(() => {
                this.details = result;
            });
        }
        catch (e) {
            if (seq !== this.fetchSequence)
                return;
            console.error("[PatientHistoryDetails] Failed to load details", e);
            runInAction(() => {
                this.details = null;
            });
        }
    }
    get isLoading() {
        return this.details === NOT_LOADED;
    }
    get hasError() {
        return this.details === null;
    }
    get record() {
        if (this.details === NOT_LOADED || this.details === null)
            return null;
        return this.details;
    }
    get isVisit() {
        var _a;
        return ((_a = this.record) === null || _a === void 0 ? void 0 : _a.type) === HealthWalletRecordType.VISIT;
    }
    get hasVaccinationInsight() {
        if (!this.record || this.record.type !== HealthWalletRecordType.IMMUNIZATION)
            return false;
        return this.record.hasInsight === true;
    }
    get vaccinationName() {
        var _a;
        if (!this.record || this.record.type !== HealthWalletRecordType.IMMUNIZATION)
            return "";
        return (_a = this.record.vaccinationName) !== null && _a !== void 0 ? _a : "";
    }
    get category() {
        var _a, _b, _c, _d, _e, _f;
        if (!this.record)
            return HistoryCategory.PRIMARY_CARE;
        if (this.record.type === HealthWalletRecordType.VISIT) {
            const visit = this.record;
            return (_b = VISIT_TYPE_TO_CATEGORY[(_a = visit.visitType) !== null && _a !== void 0 ? _a : ""]) !== null && _b !== void 0 ? _b : HistoryCategory.PRIMARY_CARE;
        }
        if (this.record.type === HealthWalletRecordType.IMMUNIZATION) {
            const immunization = this.record;
            return (_d = IMMUNIZATION_TYPE_TO_CATEGORY[(_c = immunization.immunizationType) !== null && _c !== void 0 ? _c : ""]) !== null && _d !== void 0 ? _d : HistoryCategory.VACCINATION;
        }
        const proc = this.record;
        return (_f = PROCEDURE_TYPE_TO_CATEGORY[(_e = proc.procedureType) !== null && _e !== void 0 ? _e : ""]) !== null && _f !== void 0 ? _f : HistoryCategory.PROCEDURE;
    }
    get heroTitle() {
        var _a, _b, _c;
        if (!this.record)
            return "";
        if (this.record.type === HealthWalletRecordType.VISIT) {
            return visitTypeDisplay((_a = this.record.visitType) !== null && _a !== void 0 ? _a : "");
        }
        if (this.record.type === HealthWalletRecordType.IMMUNIZATION) {
            return immunizationTypeDisplay((_b = this.record.immunizationType) !== null && _b !== void 0 ? _b : "");
        }
        return procedureTypeDisplay((_c = this.record.procedureType) !== null && _c !== void 0 ? _c : "");
    }
    get heroSubtitle() {
        var _a, _b, _c, _d;
        return (_d = (_b = (_a = this.record) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : (_c = this.record) === null || _c === void 0 ? void 0 : _c.description) !== null && _d !== void 0 ? _d : "";
    }
    get heroDate() {
        var _a;
        return formatDisplayDate((_a = this.record) === null || _a === void 0 ? void 0 : _a.date);
    }
    get heroSource() {
        return provenanceSourceDisplay(ProvenanceSource.CLINICAL_RECORDS);
    }
    get provider() {
        var _a, _b;
        return (_b = (_a = this.record) === null || _a === void 0 ? void 0 : _a.provider) !== null && _b !== void 0 ? _b : null;
    }
    get measurements() {
        var _a, _b, _c;
        return (_c = (_b = (_a = this.record) === null || _a === void 0 ? void 0 : _a.whatWasMeasured) === null || _b === void 0 ? void 0 : _b.measurements) !== null && _c !== void 0 ? _c : [];
    }
    get documents() {
        var _a, _b, _c;
        return (_c = (_b = (_a = this.record) === null || _a === void 0 ? void 0 : _a.documents) === null || _b === void 0 ? void 0 : _b.documents) !== null && _c !== void 0 ? _c : [];
    }
    get provenance() {
        var _a, _b;
        return (_b = (_a = this.record) === null || _a === void 0 ? void 0 : _a.provenance) !== null && _b !== void 0 ? _b : null;
    }
    get provenanceSource() {
        var _a, _b;
        return provenanceSourceDisplay((_b = (_a = this.provenance) === null || _a === void 0 ? void 0 : _a.source) !== null && _b !== void 0 ? _b : ProvenanceSource.CLINICAL_RECORDS);
    }
    get provenanceOrganizationName() {
        var _a, _b;
        return (_b = (_a = this.provenance) === null || _a === void 0 ? void 0 : _a.organizationName) !== null && _b !== void 0 ? _b : "-";
    }
    get provenanceRetrievedAt() {
        var _a;
        if (!((_a = this.provenance) === null || _a === void 0 ? void 0 : _a.retrievedAt))
            return "-";
        return formatDisplayDate(this.provenance.retrievedAt);
    }
    get provenanceLastViewed() {
        var _a;
        if (!((_a = this.provenance) === null || _a === void 0 ? void 0 : _a.lastViewedAt))
            return "-";
        return formatDisplayDate(this.provenance.lastViewedAt);
    }
    async onPrintRecord() {
        this.moreMenuController.closeMenu();
        this.isPrinting = true;
        try {
            await printScreen({});
        }
        finally {
            runInAction(() => {
                this.isPrinting = false;
            });
        }
    }
    async onDownloadDocument(doc) {
        if (!this.authManager.id)
            return;
        this.downloadingDocumentId = doc.id;
        const wantNewTab = isBrowserDisplayableContentType(doc.contentType);
        const pendingTab = wantNewTab ? window.open("", "_blank") : null;
        const popupBlocked = wantNewTab && pendingTab === null;
        try {
            const blob = await this.healthWalletService.downloadDocument(this.authManager.id, doc.id);
            const typedBlob = blob.type ? blob : new Blob([blob], { type: doc.contentType.split(";")[0].trim() });
            if (pendingTab) {
                if (isSandboxedHtmlContentType(doc.contentType)) {
                    await openSanitizedHtmlInTab(pendingTab, typedBlob, doc.name);
                }
                else {
                    const url = URL.createObjectURL(typedBlob);
                    pendingTab.location.href = url;
                    setTimeout(() => URL.revokeObjectURL(url), 30000);
                }
            }
            else {
                const url = URL.createObjectURL(typedBlob);
                downloadURL(url, doc.name);
                URL.revokeObjectURL(url);
                if (popupBlocked) {
                    this.bannerUIManager.displayBanner({
                        text: t("Popup blocked — document downloaded instead. Allow popups to view documents in your browser."),
                        type: "INFO",
                    });
                }
            }
        }
        catch (e) {
            pendingTab === null || pendingTab === void 0 ? void 0 : pendingTab.close();
            console.error("[PatientHistoryDetails] Failed to download document", e);
            this.bannerUIManager.displayBanner({
                text: t("Failed to download document. Please try again"),
                type: "ERROR",
            });
        }
        finally {
            runInAction(() => {
                this.downloadingDocumentId = null;
            });
        }
    }
    openMeasurementDialog(measurement) {
        const { observationId } = measurement;
        if (!observationId)
            return;
        this.selectedMeasurementName = measurement.name;
        this.selectedMeasurementObservationId = observationId;
        this.measurementInsight = NOT_LOADED;
        this.measurementDialogOpen = true;
    }
    closeMeasurementDialog() {
        this.insightFetchSequence++;
        this.measurementDialogOpen = false;
        this.selectedMeasurementName = null;
        this.selectedMeasurementObservationId = null;
        this.measurementInsight = NOT_LOADED;
    }
    async loadMeasurementInsight(observationId) {
        var _a;
        if (!this.authManager.id)
            return;
        const seq = ++this.insightFetchSequence;
        runInAction(() => {
            this.measurementInsight = NOT_LOADED;
        });
        try {
            const result = await this.healthWalletService.getMeasurementInsight(this.authManager.id, observationId, this.browserLanguageUIManager.currentLanguage, (_a = this.unitSystemManager.unitSystem) !== null && _a !== void 0 ? _a : undefined);
            if (seq !== this.insightFetchSequence)
                return;
            runInAction(() => {
                this.measurementInsight = result;
            });
        }
        catch (e) {
            if (seq !== this.insightFetchSequence)
                return;
            console.error("[PatientHistoryDetails] Failed to load measurement insight", e);
            runInAction(() => {
                this.measurementInsight = null;
            });
        }
    }
    get insightLoading() {
        return this.measurementInsight === NOT_LOADED;
    }
    get insightError() {
        return this.measurementInsight === null;
    }
    get insightData() {
        if (this.measurementInsight === NOT_LOADED || this.measurementInsight === null)
            return null;
        return this.measurementInsight;
    }
    get insightCategory() {
        var _a, _b;
        return (_b = (_a = this.insightData) === null || _a === void 0 ? void 0 : _a.category) !== null && _b !== void 0 ? _b : "";
    }
    get insightDescription() {
        var _a, _b;
        return (_b = (_a = this.insightData) === null || _a === void 0 ? void 0 : _a.simpleDescription) !== null && _b !== void 0 ? _b : "";
    }
    get insightWhyItMatters() {
        var _a, _b;
        return (_b = (_a = this.insightData) === null || _a === void 0 ? void 0 : _a.whyItMatters) !== null && _b !== void 0 ? _b : "";
    }
    get insightSource() {
        var _a, _b;
        return (_b = (_a = this.insightData) === null || _a === void 0 ? void 0 : _a.source) !== null && _b !== void 0 ? _b : "";
    }
    get insightRanges() {
        var _a, _b;
        return (_b = (_a = this.insightData) === null || _a === void 0 ? void 0 : _a.referenceRanges) !== null && _b !== void 0 ? _b : [];
    }
    get insightRangesSource() {
        var _a, _b;
        return (_b = (_a = this.insightData) === null || _a === void 0 ? void 0 : _a.referenceRangeSource) !== null && _b !== void 0 ? _b : null;
    }
    get showInsightRanges() {
        var _a, _b;
        return (_b = (_a = this.insightData) === null || _a === void 0 ? void 0 : _a.referenceRangeApplicable) !== null && _b !== void 0 ? _b : false;
    }
    formatDocumentSize(doc) {
        return formatFileSize(doc.sizeInBytes);
    }
    formatDocumentDate(doc) {
        return formatDisplayDate(doc.date);
    }
    openVaccinationInsightDialog() {
        if (!this.resourceId)
            return;
        this.selectedVaccinationTitle = this.vaccinationName || this.heroSubtitle || this.heroTitle || null;
        this.vaccinationInsight = NOT_LOADED;
        this.vaccinationInsightDialogOpen = true;
    }
    closeVaccinationInsightDialog() {
        this.vaccinationInsightFetchSequence++;
        this.vaccinationInsightDialogOpen = false;
        this.selectedVaccinationTitle = null;
        this.vaccinationInsight = NOT_LOADED;
    }
    async loadVaccinationInsight(resourceId) {
        if (!this.authManager.id)
            return;
        const seq = ++this.vaccinationInsightFetchSequence;
        runInAction(() => {
            this.vaccinationInsight = NOT_LOADED;
        });
        try {
            const result = await this.healthWalletService.getVaccinationInsight(this.authManager.id, resourceId, this.browserLanguageUIManager.currentLanguage);
            if (seq !== this.vaccinationInsightFetchSequence)
                return;
            runInAction(() => {
                this.vaccinationInsight = result;
            });
        }
        catch (e) {
            if (seq !== this.vaccinationInsightFetchSequence)
                return;
            console.error("[PatientHistoryDetails] Failed to load vaccination insight", e);
            runInAction(() => {
                this.vaccinationInsight = null;
            });
        }
    }
    get vaccinationInsightLoading() {
        return this.vaccinationInsight === NOT_LOADED;
    }
    get vaccinationInsightError() {
        return this.vaccinationInsight === null;
    }
    get vaccinationInsightData() {
        if (this.vaccinationInsight === NOT_LOADED || this.vaccinationInsight === null)
            return null;
        return this.vaccinationInsight;
    }
    get vaccinationInsightName() {
        var _a, _b;
        return (_b = (_a = this.vaccinationInsightData) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : "";
    }
    get vaccinationInsightDescription() {
        var _a, _b;
        return (_b = (_a = this.vaccinationInsightData) === null || _a === void 0 ? void 0 : _a.simpleDescription) !== null && _b !== void 0 ? _b : "";
    }
    get vaccinationInsightWhyItMatters() {
        var _a, _b;
        return (_b = (_a = this.vaccinationInsightData) === null || _a === void 0 ? void 0 : _a.whyItMatters) !== null && _b !== void 0 ? _b : "";
    }
    get vaccinationInsightSource() {
        var _a, _b;
        return (_b = (_a = this.vaccinationInsightData) === null || _a === void 0 ? void 0 : _a.source) !== null && _b !== void 0 ? _b : "";
    }
}
__decorate([
    observable
], PatientTimelineDetailsViewModel.prototype, "details", void 0);
__decorate([
    observable
], PatientTimelineDetailsViewModel.prototype, "isPrinting", void 0);
__decorate([
    observable
], PatientTimelineDetailsViewModel.prototype, "downloadingDocumentId", void 0);
__decorate([
    observable
], PatientTimelineDetailsViewModel.prototype, "measurementDialogOpen", void 0);
__decorate([
    observable
], PatientTimelineDetailsViewModel.prototype, "selectedMeasurementName", void 0);
__decorate([
    observable
], PatientTimelineDetailsViewModel.prototype, "selectedMeasurementObservationId", void 0);
__decorate([
    observable
], PatientTimelineDetailsViewModel.prototype, "measurementInsight", void 0);
__decorate([
    observable
], PatientTimelineDetailsViewModel.prototype, "vaccinationInsightDialogOpen", void 0);
__decorate([
    observable
], PatientTimelineDetailsViewModel.prototype, "selectedVaccinationTitle", void 0);
__decorate([
    observable
], PatientTimelineDetailsViewModel.prototype, "vaccinationInsight", void 0);
__decorate([
    computed
], PatientTimelineDetailsViewModel.prototype, "hasLoadedFeatures", null);
__decorate([
    computed
], PatientTimelineDetailsViewModel.prototype, "isHealthWalletEnabled", null);
__decorate([
    computed
], PatientTimelineDetailsViewModel.prototype, "resourceId", null);
__decorate([
    computed
], PatientTimelineDetailsViewModel.prototype, "recordType", null);
__decorate([
    computed
], PatientTimelineDetailsViewModel.prototype, "isLoading", null);
__decorate([
    computed
], PatientTimelineDetailsViewModel.prototype, "hasError", null);
__decorate([
    computed
], PatientTimelineDetailsViewModel.prototype, "record", null);
__decorate([
    computed
], PatientTimelineDetailsViewModel.prototype, "isVisit", null);
__decorate([
    computed
], PatientTimelineDetailsViewModel.prototype, "hasVaccinationInsight", null);
__decorate([
    computed
], PatientTimelineDetailsViewModel.prototype, "vaccinationName", null);
__decorate([
    computed
], PatientTimelineDetailsViewModel.prototype, "category", null);
__decorate([
    computed
], PatientTimelineDetailsViewModel.prototype, "heroTitle", null);
__decorate([
    computed
], PatientTimelineDetailsViewModel.prototype, "heroSubtitle", null);
__decorate([
    computed
], PatientTimelineDetailsViewModel.prototype, "heroDate", null);
__decorate([
    computed
], PatientTimelineDetailsViewModel.prototype, "heroSource", null);
__decorate([
    computed
], PatientTimelineDetailsViewModel.prototype, "provider", null);
__decorate([
    computed
], PatientTimelineDetailsViewModel.prototype, "measurements", null);
__decorate([
    computed
], PatientTimelineDetailsViewModel.prototype, "documents", null);
__decorate([
    computed
], PatientTimelineDetailsViewModel.prototype, "provenance", null);
__decorate([
    computed
], PatientTimelineDetailsViewModel.prototype, "provenanceSource", null);
__decorate([
    computed
], PatientTimelineDetailsViewModel.prototype, "provenanceOrganizationName", null);
__decorate([
    computed
], PatientTimelineDetailsViewModel.prototype, "provenanceRetrievedAt", null);
__decorate([
    computed
], PatientTimelineDetailsViewModel.prototype, "provenanceLastViewed", null);
__decorate([
    action
], PatientTimelineDetailsViewModel.prototype, "onPrintRecord", null);
__decorate([
    action
], PatientTimelineDetailsViewModel.prototype, "onDownloadDocument", null);
__decorate([
    action
], PatientTimelineDetailsViewModel.prototype, "openMeasurementDialog", null);
__decorate([
    action
], PatientTimelineDetailsViewModel.prototype, "closeMeasurementDialog", null);
__decorate([
    computed
], PatientTimelineDetailsViewModel.prototype, "insightLoading", null);
__decorate([
    computed
], PatientTimelineDetailsViewModel.prototype, "insightError", null);
__decorate([
    computed
], PatientTimelineDetailsViewModel.prototype, "insightData", null);
__decorate([
    computed
], PatientTimelineDetailsViewModel.prototype, "insightCategory", null);
__decorate([
    computed
], PatientTimelineDetailsViewModel.prototype, "insightDescription", null);
__decorate([
    computed
], PatientTimelineDetailsViewModel.prototype, "insightWhyItMatters", null);
__decorate([
    computed
], PatientTimelineDetailsViewModel.prototype, "insightSource", null);
__decorate([
    computed
], PatientTimelineDetailsViewModel.prototype, "insightRanges", null);
__decorate([
    computed
], PatientTimelineDetailsViewModel.prototype, "insightRangesSource", null);
__decorate([
    computed
], PatientTimelineDetailsViewModel.prototype, "showInsightRanges", null);
__decorate([
    action
], PatientTimelineDetailsViewModel.prototype, "openVaccinationInsightDialog", null);
__decorate([
    action
], PatientTimelineDetailsViewModel.prototype, "closeVaccinationInsightDialog", null);
__decorate([
    computed
], PatientTimelineDetailsViewModel.prototype, "vaccinationInsightLoading", null);
__decorate([
    computed
], PatientTimelineDetailsViewModel.prototype, "vaccinationInsightError", null);
__decorate([
    computed
], PatientTimelineDetailsViewModel.prototype, "vaccinationInsightData", null);
__decorate([
    computed
], PatientTimelineDetailsViewModel.prototype, "vaccinationInsightName", null);
__decorate([
    computed
], PatientTimelineDetailsViewModel.prototype, "vaccinationInsightDescription", null);
__decorate([
    computed
], PatientTimelineDetailsViewModel.prototype, "vaccinationInsightWhyItMatters", null);
__decorate([
    computed
], PatientTimelineDetailsViewModel.prototype, "vaccinationInsightSource", null);
